import {
  Button,
  Divider,
  EButtonColorVariant,
  ETextStyleVariant,
  LinkButton,
  RadioSelector,
  SelectorGroup,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { RENTER_AGE_OPTIONS } from '@/constants/driverAge';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useInitialDriverAge } from '@/hooks/useInitialInputs';
import { EHeaderSearchFilters, setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { getHeaderSearchDriverAgeFilter } from '@/redux/selectors/globalHeader';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';

import { FilterContent, TFilterContentSharedProps } from './FilterContent';

type TDriverAgeFilterContentProps = TFilterContentSharedProps & {
  onDataSelection?: () => void;
};

export const DriverAgeFilterContent = ({
  onBack,
  onDataSelection,
  ...props
}: TDriverAgeFilterContentProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { isAboveDesktop } = useBreakpoint();

  const currentDriverAge = useSelector(getHeaderSearchDriverAgeFilter);
  const { hasChanges, isInResetState, resetToInitial } = useInitialDriverAge(
    currentDriverAge,
    props.isOpened,
  );

  const handleAgeSelection = useCallback(
    (age: number | undefined) => {
      const oldSelection = { driverAge: currentDriverAge };
      const newSelection = { driverAge: age };

      dispatch(
        setHeaderSearchFilter({
          [EHeaderSearchFilters.DRIVER_AGE]: age,
        }),
      );

      props.onTrackFilterToggled?.(oldSelection, newSelection, ESearchWidgetFilter.DriverAge);

      if (isAboveDesktop) {
        onDataSelection?.();
      }
    },
    [currentDriverAge, dispatch, isAboveDesktop, onDataSelection, props],
  );

  // CSS styles for horizontal radio layout - targets SelectorGroup's internal structure
  const horizontalRadioStyles = `
    .horizontal-radio-group [role="radiogroup"] > div {
      display: flex !important;
      flex-direction: row !important;
      gap: 1rem;
    }
    .horizontal-radio-group [role="radiogroup"] > div > div {
      flex: 1;
    }
  `;

  const getContent = () => {
    // Desktop uses simple button list
    if (isAboveDesktop) {
      return (
        <div className="flex flex-col">
          {RENTER_AGE_OPTIONS.map((option, index) => (
            <React.Fragment key={option.value}>
              <button
                onClick={() => handleAgeSelection(option.value)}
                className="w-full px-6 py-4 text-left hover:bg-gray-10 focus:bg-gray-10 focus:outline-none transition-colors">
                <Text variant={ETextStyleVariant.MediumRegular}>{option.label}</Text>
              </button>
              {index < RENTER_AGE_OPTIONS.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </div>
      );
    }

    // Mobile uses horizontal radio layout like DriverAgeFilter
    return (
      <>
        <style>{horizontalRadioStyles}</style>
        <div>
          <p className="mb-4 text-sm text-gray-600">
            <Text variant={ETextStyleVariant.SmallRegular}>
              {intl.formatMessage({
                defaultMessage:
                  'Select the age range that applies to the primary driver on this trip',
                id: 'DczvHF',
              })}
            </Text>
          </p>
          <div className="horizontal-radio-group">
            <SelectorGroup
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Driver age selection',
                id: 'ps487E',
              })}
              onChange={(value: string) => handleAgeSelection(Number(value))}
              value={currentDriverAge?.toString() || ''}>
              {[...RENTER_AGE_OPTIONS].reverse().map(option => (
                <RadioSelector
                  key={option.value}
                  label={option.label}
                  value={option.value.toString()}
                  name="driverAge"
                  ariaLabel={`Select driver age ${option.label}`}
                  showIndicator
                  withBorder
                />
              ))}
            </SelectorGroup>
          </div>
        </div>
      </>
    );
  };

  return (
    <FilterContent
      onBack={() => {
        const initialValue = resetToInitial();
        handleAgeSelection(initialValue);
        onBack?.();
      }}
      header={
        <>
          {intl.formatMessage({
            defaultMessage: "Add driver's age",
            id: 'BCEZz6',
            description: 'Home Search Widget > Driver Age Filter Title',
          })}
        </>
      }
      content={getContent()}
      footerContent={
        !isAboveDesktop ? (
          <FilterDriverAgeFooter
            onClearDriverAge={() => {
              handleAgeSelection(undefined);
            }}
            onDone={() => {
              props.onDone?.();
            }}
            hasChanges={hasChanges}
            isInResetState={isInResetState}
          />
        ) : null
      }
      {...props}
    />
  );
};

type TFilterDriverAgeFooterProps = {
  onClearDriverAge: () => void;
  onDone: () => void;
  hasChanges: boolean;
  isInResetState: boolean;
};

const FilterDriverAgeFooter = ({
  onClearDriverAge,
  onDone,
  hasChanges,
  isInResetState,
}: TFilterDriverAgeFooterProps) => {
  const intl = useIntl();
  const hasSingleAction = [onClearDriverAge, onDone].filter(x => x).length === 1;

  return (
    <div className="w-full">
      <Divider />
      <div
        className={`flex items-center ${hasSingleAction ? 'justify-end' : 'justify-between'} p-4 lg:hidden`}>
        {onClearDriverAge && (
          <LinkButton
            label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
            onClick={onClearDriverAge}
            disabled={isInResetState}
          />
        )}
        {onDone && (
          <Button
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
            onClick={onDone}
            disabled={!hasChanges}
          />
        )}
      </div>
    </div>
  );
};
